import { Formik, FormikInsured, YesNoAnswersNew } from "src/types";
import { InputDate, InputText, InputChip } from "src/ui/widgets";
import { AnyObjectSchema } from "yup";
import { useInsured } from "./insured.hook";
import { ChangeEvent } from "react";
import { FormikErrors, FormikTouched } from "formik";

export function Insured({
  formik,
  validationSchema,
  insuredTouched,
  insuredValue,
  insuredError,
  base,
  onIdentificationFieldsChanges,
}: {
  formik: Formik<any>;
  insuredTouched: FormikTouched<FormikInsured>;
  insuredValue: FormikInsured;
  insuredError: FormikErrors<FormikInsured>;
  validationSchema: AnyObjectSchema;
  base: string;
  onIdentificationFieldsChanges?: (insured: FormikInsured) => void;
}) {
  const { handleChange, handleBlur, setFieldValue, setFieldTouched } = formik;

  const {
    t,
    genderOptions,
    fcQuestionOptions,
    nationalityOptions,
    onSelectFcQuestion,
  } = useInsured({ base, formik, onIdentificationFieldsChanges });

  return (
    <>
      <div className="d-block d-md-flex">
        <div className="col-sm-12 col-md-6 p-0 pe-md-3">
          <InputText
            name={`${base}insuredName`}
            handleChange={handleChange}
            handleBlur={handleBlur}
            validationSchema={validationSchema?.fields?.insuredList?.innerType}
            touched={insuredTouched?.insuredName}
            error={insuredError?.insuredName}
            value={insuredValue?.insuredName}
            label={`${t("data_entry_step_2.insured_name")}*`}
            placeholder={t("data_entry_step_2.insured_name_placeholder")}
          />
        </div>
        <div className="col-sm-12 col-md-6 p-0 ps-md-3">
          <InputText
            name={`${base}insuredSurname`}
            handleChange={handleChange}
            handleBlur={handleBlur}
            validationSchema={validationSchema?.fields?.insuredList?.innerType}
            touched={insuredTouched?.insuredSurname}
            value={insuredValue?.insuredSurname}
            error={insuredError?.insuredSurname}
            label={`${t("data_entry_step_2.insured_surname")}*`}
            placeholder={t("data_entry_step_2.insured_surname_placeholder")}
          />
        </div>
      </div>

      <div className="d-block">
        <div className="col-sm-12 col-md-6 p-0 pe-md-3">
          <InputChip
            name={`${base}insuredNationality`}
            handleChange={handleChange}
            handleBlur={handleBlur}
            options={nationalityOptions}
            validationSchema={validationSchema?.fields?.insuredList?.innerType}
            touched={insuredTouched?.insuredNationality}
            value={insuredValue?.insuredNationality}
            error={insuredError?.insuredNationality}
            label={t("data_entry_step_2.insured_nationality")}
            classesLabel="m-0 p-0 font-medium-s d-flex align-items-center mb-2"
            classesOption="py-1"
            classes="col-7 mb-4"
          />
        </div>

        <div className="col-sm-12 col-md-6 p-0 pe-md-3">
          <InputChip
            name={`${base}insuredFcQuestion`}
            handleChange={(event: ChangeEvent<HTMLInputElement>) => {
              onSelectFcQuestion(event);
              handleChange(event);
            }}
            handleBlur={handleBlur}
            options={fcQuestionOptions}
            validationSchema={validationSchema?.fields?.insuredList?.innerType}
            touched={insuredTouched?.insuredFcQuestion}
            value={insuredValue?.insuredFcQuestion}
            error={insuredError?.insuredFcQuestion}
            label={`${t("data_entry_step_2.insured_fc_question")}`}
            classesLabel="m-0 p-0 font-medium-s d-flex align-items-center mb-2"
            classesOption="py-1"
            classes="col-7 mb-4"
          />
        </div>
        {insuredValue.insuredFcQuestion === YesNoAnswersNew.YES && (
          <div className="d-block d-md-flex">
            <div className="col-sm-12 col-md-6 p-0 pe-md-3">
              <InputText
                name={`${base}insuredFc`}
                handleChange={handleChange}
                handleBlur={handleBlur}
                validationSchema={validationSchema}
                touched={insuredTouched?.insuredFc}
                value={insuredValue.insuredFc?.toUpperCase()}
                error={insuredError?.insuredFc}
                label={`${t("data_entry_step_2.insured_fc")}*`}
                placeholder={t("data_entry_step_2.insured_fc_placeholder")}
              />
            </div>
          </div>
        )}
        {insuredValue.insuredFcQuestion === YesNoAnswersNew.NO && (
          <div>
            <div className="d-block d-md-flex">
              <div className="col-sm-12 col-md-6 p-0 pe-md-3">
                <InputDate
                  name={`${base}insuredBirth`}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  validationSchema={validationSchema}
                  touched={insuredTouched?.insuredBirth}
                  value={insuredValue?.insuredBirth}
                  error={insuredError?.insuredBirth}
                  label={`${t("data_entry_step_2.insured_birth")}*`}
                  placeholder={t("data_entry_step_2.insured_birth_placeholder")}
                  disabledTo={new Date()}
                  classes="m-0 p-0"
                />
              </div>

              <div className="col-sm-12 col-md-6 p-0 ps-md-3">
                <InputText
                  name={`${base}insuredBirthNation`}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  validationSchema={validationSchema}
                  touched={insuredTouched?.insuredBirthNation}
                  value={insuredValue?.insuredBirthNation}
                  error={insuredError?.insuredBirthNation}
                  label={`${t("data_entry_step_2.insured_birth_nation")}*`}
                  placeholder={t(
                    "data_entry_step_2.insured_birth_nation_placeholder"
                  )}
                />
              </div>
            </div>

            <div className="d-block d-md-flex">
              <div className="col-sm-12 col-md-6 p-0 pe-md-3">
                <InputText
                  name={`${base}insuredBirthPlace`}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  validationSchema={validationSchema}
                  touched={insuredTouched?.insuredBirthPlace}
                  value={insuredValue?.insuredBirthPlace}
                  error={insuredError?.insuredBirthPlace}
                  label={`${t("data_entry_step_2.insured_birth_place")}*`}
                  placeholder={t(
                    "data_entry_step_2.insured_birth_place_placeholder"
                  )}
                />
              </div>
            </div>

            <div className="d-block d-md-flex">
              <div className="col-sm-12 col-md-6 p-0 pe-md-3">
                <InputChip
                  name={`${base}insuredGender`}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  options={genderOptions}
                  validationSchema={validationSchema}
                  touched={insuredTouched?.insuredGender}
                  value={insuredValue?.insuredGender}
                  error={insuredError?.insuredGender}
                  label={`${t("data_entry_step_2.insured_gender")}*`}
                  classesLabel="m-0 p-0 font-medium-s d-flex align-items-center mb-3"
                  classesOption="py-1"
                  classes="col-7 mb-4"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
